import { useEffect, useMemo } from "react";
import { useNewTrialState } from "../legacy";
import { toMapRatesAndDosages } from "../utils/nutrientMapRates";
import { type NutrientState } from "./useNutrientState";

const NUTRIENT_TAB = "nutrient";
const DEFAULT_TAB = "seed";

/**
 * Keeps the shared map in step with the Nutrient tab, the same way the old
 * Rates & Dosage screen did.
 *
 * The map reads the shared trial state, not this page's state: it colours each
 * plot from `ratesDosage.rates_and_dosages` when the active rates tab is
 * "nutrient", and draws the plots switched on with the eye button with a
 * thicker outline. So while the Nutrient tab is showing, its rates are
 * published in that shape; on any other tab they are cleared and the tab
 * setting is put back.
 */
const useNutrientMapSync = (
  state: NutrientState,
  isNutrientTabActive: boolean
): void => {
  const { setRatesDosage, setActiveRatesTab, setIsFertilizerTabActive } =
    useNewTrialState();
  const { groups, applyByPlot, totalRate, highlightedPlotIds } = state;

  const mapRates = useMemo(
    () =>
      isNutrientTabActive
        ? toMapRatesAndDosages(
            groups,
            applyByPlot,
            totalRate,
            highlightedPlotIds
          )
        : [],
    [isNutrientTabActive, groups, applyByPlot, totalRate, highlightedPlotIds]
  );

  useEffect(() => {
    setRatesDosage((previous) => ({
      ...previous,
      // The wizard's type says `plot_id` is a number, but its own rows hold the
      // string "{groupId}-{row}-{col}", which is what is published here.
      rates_and_dosages:
        mapRates as unknown as typeof previous.rates_and_dosages,
    }));
  }, [mapRates, setRatesDosage]);

  useEffect(() => {
    if (!isNutrientTabActive) {
      return;
    }
    setActiveRatesTab(NUTRIENT_TAB);
    setIsFertilizerTabActive(true);
    return () => {
      setActiveRatesTab(DEFAULT_TAB);
      setIsFertilizerTabActive(false);
    };
  }, [isNutrientTabActive, setActiveRatesTab, setIsFertilizerTabActive]);
};

export default useNutrientMapSync;
